




























































import { Vue, Prop, Ref, Component } from "vue-property-decorator";

import Report, { ExtendedReportUsage } from "../../data/models/Report";
import { formatReportDateText, formatReportLabel, getReportUsage, isExternalReportUsage } from "@/utils/ReportLabel";
import { formatJsDate } from "../../utils/DateFormatter";
import { getDownloadUrl } from "@/data/repository/FirebaseImageRepository";
import { deleteReport } from "@/data/repository/ReportRepository";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { findNickname } from "@/data/repository/UserRepository";
import { setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import {
  DownloadFileType,
  getFileTypeIcon,
  getFileTypeIconForFileName,
} from "@/utils/FileTypeIcons";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ReportItemComponent extends Vue {
  @Prop() report!: Report;
  @Prop() elevation!: number;
  @Prop() showDeleteReportButton!: boolean;
  @Prop({ default: false })
  isPortalOnlyProject!: boolean;

  @Prop({ default: "" })
  backgroundStyle!: string;

  @Prop({ default: false })
  forceUpdateBackgroundColor!: boolean;

  downloadUrl = "";

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  async onDeleteReport() {
    const result = await this.confirmationDialogComponent.show({
      title: this.$t("report.deleteReportTitle").toString(),
      message: this.$t("report.deleteReportMessage").toString(),
    });

    if (result) {
      await deleteReport(this.report);
    }
  }

  get reportUsage() {
    return getReportUsage(this.report);
  }

  /** Phone layout (below 600 px): title line with the delete icon, the date in grey below. */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  get shouldIndentItem() {
    return this.reportUsage !== ExtendedReportUsage.INTERNAL && !this.isPortalOnlyProject;
  }

  get reportText() {
    return formatReportLabel(this.report, (key, params) => this.$t(key, params).toString());
  }

  get isReportUsageExternal() {
    return isExternalReportUsage(this.reportUsage);
  }

  get dateText() {
    const date = formatReportDateText(
      formatJsDate(this.report.reportDate.toDate()),
      this.report.lastTimeSend ? formatJsDate(this.report.lastTimeSend.toDate()) : null,
      this.isReportUsageExternal,
      (key, params) => this.$t(key, params).toString()
    );

    const nickname = findNickname(this.report.createdBy);

    return nickname ? `${date} (${nickname})` : date;
  }

  async mounted() {
    if (this.report.storageLocation) {
      try {
        this.downloadUrl = await getDownloadUrl(this.report.storageLocation);
      } catch (error) {
        // Report file missing in storage — leave downloadUrl empty
      }
    }
  }

  /** Whether this report is a PDE JSON file that can be opened in the viewer */
  get isPdeReport(): boolean {
    const usage = this.report.extendedReportUsage ?? this.report.usage;
    return usage === ExtendedReportUsage.SERVICE_JSON;
  }

  /** Whether this report is a Beschreibung CSV file */
  get isBeschreibungReport(): boolean {
    const usage = this.report.extendedReportUsage ?? this.report.usage;
    if (usage !== ExtendedReportUsage.SERVICE) return false;
    const fileName = this.report.fileName ?? this.report.storageLocation ?? "";
    return fileName.toLowerCase().endsWith(".csv");
  }

  /**
   * Icon of the file behind this row.
   *
   * The two inline-viewer kinds are known by their usage, so they are answered directly; everything
   * else is matched on the stored file name, which is what makes an ERP mirror's `.PDF` and a
   * generated `.pdf` the same thing. A report with neither reads as the neutral file icon rather than
   * claiming to be a PDF.
   */
  get fileTypeIcon(): string {
    if (this.isPdeReport) {
      return getFileTypeIcon(DownloadFileType.JSON);
    }

    if (this.isBeschreibungReport) {
      return getFileTypeIcon(DownloadFileType.CSV);
    }

    return getFileTypeIconForFileName(this.report.fileName ?? this.report.storageLocation);
  }

  /** Whether this report opens in an inline viewer instead of downloading */
  get hasInlineViewer(): boolean {
    return this.isPdeReport || this.isBeschreibungReport;
  }

  onReportClick() {
    this.markReportAsVisited();
    if (this.isPdeReport) {
      this.$emit("openPdeViewer", this.report);
    } else if (this.isBeschreibungReport) {
      this.$emit("openBeschreibungViewer", this.report);
    }
  }

  markReportAsVisited() {
    setLastVisitedItem(
      this.report.id,
      StorageKeyLastVisitedItemLevel.LEVEL_5, 
    );

    this.$emit("openReport");
  }
}
